import { ReactComponent as IconDragHandle } from "assets/svg/drag-handle-vertical.svg";
import { ReactComponent as OutlineEyeClosed } from "assets/svg/outline-eye-closed.svg";
import { ReactComponent as OutlineEyeOpen } from "assets/svg/outline-eye-open.svg";
import { ReactComponent as OutlineTrash1 } from "assets/svg/outline-trash-1.svg";
import IconButton from "components/icon-button";
import SessionTypeColorBadge from "components/session-type-color-badge/SessionTypeColorBadge";
import useListReordering, { ReorderHandler } from "hooks/use-list-reordering";
import messages from "messages";
import getClasses from "utils/getClasses";
import isFreeOfCharge from "../../../../../../utils/isFreeOfCharge";
import styles from "./SessionTypeListItem.module.scss";

type Props = {
  sessionType: SessionType;
  index: number;
  onChange: (arg: SessionType) => void;
  onEdit: () => void;
  onDelete?: (arg: SessionType) => void;
  onReorder: ReorderHandler;
  variant?: "default" | "compact" | "mobile";
};

const SessionTypeListItem = ({
  sessionType,
  index,
  onChange,
  onEdit,
  onDelete,
  onReorder,
  variant = "default",
}: Props) => {
  const { ref, opacity, isAnyItemDragged } = useListReordering(
    sessionType.label,
    index,
    "session-type",
    onReorder,
  );

  return (
    <li
      className={getClasses(
        styles["container"],
        variant === "compact" && styles["container--compact"],
        variant === "mobile" && styles["container--mobile"],
        isAnyItemDragged && styles["is-any-item-dragged"],
      )}
      ref={ref}
      onClick={onEdit}
      style={{ opacity }}
    >
      <div className={styles["drag-handle"]}>
        <IconDragHandle />
      </div>

      <div className={styles["info"]}>
        <SessionTypeColorBadge colorId={sessionType.color_code} />

        <div className={styles["name"]}>{sessionType.label || "no label"}</div>

        <div className={styles["length"]}>
          {sessionType.length_min + " " + messages.minutesShort}
        </div>

        <div className={styles["price"]}>
          {isFreeOfCharge(sessionType.price)
            ? messages.free
            : `$ ${sessionType.price}`}
        </div>
      </div>

      <div className={styles["actions"]}>
        <IconButton
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();

            // `enabled` is deprecated and will be treated as a duplicate of `!invite_only`
            // The checkbox icon that controlled the field has been removed
            // The following steps are taken to ensure backwards compatibility:
            // If a session is disabled, a closed eye icon will be shown regardless of the invite_only value
            // Clicking the close eye icon will enable the session and set invite_only to false
            // Once a session has `enabled` set to true, the value will never be changed again
            // (Clicking an open eye icon will just set invite_only to true)

            const wasInviteOnly = sessionType.invite_only;
            const wasDisabled = !sessionType.enabled;

            onChange({
              ...sessionType,
              invite_only: !(wasInviteOnly || wasDisabled),
              enabled: true,
            });
          }}
        >
          {sessionType.invite_only || !sessionType.enabled ? (
            <OutlineEyeClosed />
          ) : (
            <OutlineEyeOpen />
          )}
        </IconButton>
        {onDelete && (
          <IconButton
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onDelete(sessionType);
            }}
          >
            <OutlineTrash1 />
          </IconButton>
        )}
      </div>
    </li>
  );
};

export default SessionTypeListItem;
